/**
 * Interactive map component
 * Handles rendering track icons on the world map and click interactions
 */

import { getAllTracks } from '../utils/data.js';
import { showTrackSelectionModal } from './modal.js';

/**
 * Initialize the interactive map
 * Renders all track icons on the map with click handlers
 */
export function initializeMap() {
  const tracks = getAllTracks();
  const container = document.getElementById('track-icons');
  const mapImg = document.getElementById('world-map');

  if (!container) {
    console.error('Track icons container not found');
    return;
  }

  if (!mapImg) {
    console.error('Map image not found');
    return;
  }

  // Wait for map image to load to get natural dimensions
  if (mapImg.complete && mapImg.naturalWidth !== 0) {
    renderIcons(tracks, container, mapImg);
  } else {
    mapImg.addEventListener('load', () => {
      renderIcons(tracks, container, mapImg);
    });
  }
}

/**
 * Render all track icons on the map
 * @param {Array} tracks - Array of track objects
 * @param {HTMLElement} container - Container element for icons
 * @param {HTMLImageElement} mapImg - Map image element
 */
function renderIcons(tracks, container, mapImg) {
  // Clear any existing icons
  container.innerHTML = '';

  // Get the natural (original) dimensions of the map image
  const naturalWidth = mapImg.naturalWidth;
  const naturalHeight = mapImg.naturalHeight;

  // Render each track icon
  tracks.forEach(track => {
    const iconButton = createTrackIcon(track, naturalWidth, naturalHeight);
    container.appendChild(iconButton);
  });

  console.log(`Rendered ${tracks.length} track icons on map (${naturalWidth}x${naturalHeight})`);

  // Position all icons after they're added to DOM
  positionAllIcons();

  // Listen for window resize to reposition icons
  window.addEventListener('resize', () => {
    clearTimeout(window.mapResizeTimeout);
    window.mapResizeTimeout = setTimeout(() => {
      positionAllIcons();
    }, 100);
  });
}

/**
 * Create a track icon button element
 * @param {Object} track - Track data object
 * @param {number} naturalWidth - Natural width of map image
 * @param {number} naturalHeight - Natural height of map image
 * @returns {HTMLElement} Button element for the track icon
 */
function createTrackIcon(track, naturalWidth, naturalHeight) {
  const button = document.createElement('button');
  button.className = 'track-icon-btn';

  // Store original coordinates and natural dimensions as data attributes
  // We'll use these to recalculate position on resize
  button.dataset.x = track.position.x;
  button.dataset.y = track.position.y;
  button.dataset.naturalWidth = naturalWidth;
  button.dataset.naturalHeight = naturalHeight;

  // Store icon size if specified (optional per-track override)
  if (track.iconSize) {
    button.dataset.iconSize = track.iconSize;
  }

  button.setAttribute('aria-label', `View ${track.name} races`);
  button.setAttribute('title', track.name);

  // Create icon image
  const img = document.createElement('img');
  img.src = `/icons/${track.icon}`;
  img.alt = track.name;
  img.style.width = '100%';
  img.style.height = '100%';

  // Handle image load error - show a fallback
  img.onerror = () => {
    img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"%3E%3Ccircle cx="32" cy="32" r="30" fill="%23FFD700" stroke="%23E52521" stroke-width="2"/%3E%3Ctext x="32" y="40" text-anchor="middle" font-size="32" fill="%23E52521"%3E?%3C/text%3E%3C/svg%3E';
  };

  button.appendChild(img);

  // Add click handler
  button.addEventListener('click', () => {
    showTrackSelectionModal(track);
  });

  // Position the icon after adding to DOM
  return button;
}

/**
 * Calculate the rendered map bounds within its container
 * Accounts for object-contain letterboxing
 * @param {HTMLImageElement} mapImg - Map image element
 * @returns {Object} Rendered map bounds
 */
function getRenderedMapBounds(mapImg) {
  const containerWidth = mapImg.clientWidth;
  const containerHeight = mapImg.clientHeight;
  const naturalWidth = mapImg.naturalWidth;
  const naturalHeight = mapImg.naturalHeight;

  // Calculate aspect ratios
  const containerRatio = containerWidth / containerHeight;
  const imageRatio = naturalWidth / naturalHeight;

  let renderedWidth, renderedHeight, offsetX, offsetY;

  if (containerRatio > imageRatio) {
    // Container is wider - image fills height, letterboxed on sides
    renderedHeight = containerHeight;
    renderedWidth = containerHeight * imageRatio;
    offsetX = (containerWidth - renderedWidth) / 2;
    offsetY = 0;
  } else {
    // Container is taller - image fills width, letterboxed on top/bottom
    renderedWidth = containerWidth;
    renderedHeight = containerWidth / imageRatio;
    offsetX = 0;
    offsetY = (containerHeight - renderedHeight) / 2;
  }

  return { renderedWidth, renderedHeight, offsetX, offsetY };
}

/**
 * Position a track icon based on rendered map bounds
 * @param {HTMLElement} button - The icon button element
 * @param {HTMLImageElement} mapImg - Map image element
 */
function positionIcon(button, mapImg) {
  const x = parseFloat(button.dataset.x);
  const y = parseFloat(button.dataset.y);
  const naturalWidth = parseFloat(button.dataset.naturalWidth);
  const naturalHeight = parseFloat(button.dataset.naturalHeight);

  // Get icon size from data attribute, or use default
  const baseIconSize = parseFloat(button.dataset.iconSize) || 150;

  const { renderedWidth, renderedHeight, offsetX, offsetY } = getRenderedMapBounds(mapImg);

  // Calculate scale factor
  const scale = renderedWidth / naturalWidth;

  // Calculate icon size (scale the base size)
  const iconSize = baseIconSize * scale;

  // Calculate position within the rendered map
  const scaledX = x * scale;
  const scaledY = y * scale;

  // Add offset for letterboxing
  button.style.left = `${offsetX + scaledX}px`;
  button.style.top = `${offsetY + scaledY}px`;
  button.style.width = `${iconSize}px`;
  button.style.height = `${iconSize}px`;
}

/**
 * Position all track icons
 */
function positionAllIcons() {
  const mapImg = document.getElementById('world-map');
  const container = document.getElementById('track-icons');

  if (!mapImg || !container) return;

  const icons = container.querySelectorAll('.track-icon-btn');
  icons.forEach(icon => positionIcon(icon, mapImg));
}

/**
 * Get the map container dimensions
 * Useful for responsive positioning calculations
 * @returns {Object} Width and height of the map container
 */
export function getMapDimensions() {
  const mapImg = document.getElementById('world-map');
  if (!mapImg) return { width: 0, height: 0 };

  return {
    width: mapImg.clientWidth,
    height: mapImg.clientHeight
  };
}
